/** Full-screen app menu for the mobile layout, opened from the "☰ Live account" pill. */
import { MobileDemoRow } from '../DemoAccess';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { usePendingDocuments } from '../../stores/documents';
import { useShowPro } from '../../stores/pro';
import { useInvestItems } from '../InvestMenu';
import { PromoCard } from '../MegaMenu';
import { REWARDS_PROMO } from '../TopNav';
import { BRAND, D, fmtUsd, type AccountSummary, type KycStatus, type Me } from '@platform/shared';
import { Link, navigate, usePath } from '../../lib/router';
import { setTheme, useTheme } from '../../lib/theme';
import { shortAccountId } from '../../pages/account/AccountLayout';
import { logout, setAccountKind, useAccountKind, useMe } from '../../stores/session';
import { cx, Icon, type IconName } from '../ui';
import { closeMenu, floatCls, SwitchTrack, useMenuOpen } from './chrome';
import { useAddToHomeScreen } from './InstallSheet';
import { useInvestmentTotals } from '../../stores/investments';
import { useManagedTotals } from '../../stores/managed';

const KYC: Record<KycStatus, [string, string]> = {
  none: ['Verify', 'bg-warn-bg text-warn'],
  pending: ['Verifying', 'bg-accent-bg text-accent-fg'],
  approved: ['Verified', 'bg-up-bg text-up'],
  partial: ['Partly verified', 'bg-warn-bg text-warn'],
  rejected: ['Action needed', 'bg-down-bg text-down'],
  expired: ['ID expired', 'bg-warn-bg text-warn'],
};

function MenuLink({ to, icon, children }: { to: string; icon: IconName; children: ReactNode }) {
  return (
    <Link to={to} onClick={closeMenu} className="flex h-[54px] items-center gap-3.5 border-b border-line text-[17px] text-fg no-underline">
      <Icon name={icon} size={22} />
      <span className="flex-1">{children}</span>
      <Icon name="right" size={20} strokeWidth={2.4} className="text-muted" />
    </Link>
  );
}

function InstallRow() {
  const install = useAddToHomeScreen();
  if (install.installed) return null;
  return (
    <>
      <button type="button" onClick={() => void install.start()} className="flex h-[54px] w-full items-center gap-3.5 border-b border-line text-left text-[17px] text-fg">
        <Icon name="addSquare" size={22} />
        <span className="flex-1">Add to Home Screen</span>
        <Icon name="right" size={20} strokeWidth={2.4} className="text-muted" />
      </button>
      {install.sheet}
    </>
  );
}

function DarkModeRow() {
  const theme = useTheme();
  const on = theme === 'dark';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => setTheme(on ? 'light' : 'dark')}
      className="flex h-[54px] w-full items-center gap-3.5 border-b border-line text-left text-[17px] text-fg"
    >
      <Icon name="moon" size={22} />
      <span className="flex-1">Dark mode</span>
      <SwitchTrack on={on} />
    </button>
  );
}

function AccountOption({ a, selected }: { a: AccountSummary; selected: boolean }) {
  const live = a.kind === 'live';
  // The live account's value includes money a dealer manages for the client and money in Smart Portfolios.
  const managed = useManagedTotals();
  const smart = useInvestmentTotals();
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={() => setAccountKind(a.kind)}
      className={cx('flex items-center gap-3 rounded-[18px] px-3.5 py-3 text-left text-fg', selected && 'bg-card shadow-float')}
    >
      <span className={cx('grid size-10 shrink-0 place-items-center rounded-full', live ? 'bg-up-bg text-up' : 'bg-accent-bg text-accent-fg')}>
        <Icon name={live ? 'wallet' : 'reset'} size={20} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[16px] font-semibold">{live ? 'Live account' : 'Demo account'}</span>
        <span className="block truncate text-[14px] text-muted">{live ? 'Real funds' : 'Virtual funds'}</span>
      </span>
      <span className="tnum text-right text-[16px] font-semibold">{fmtUsd(live ? D.from(a.equity).add(managed?.equity ?? D.ZERO).add(smart?.equity ?? D.ZERO) : a.equity)}</span>
      <span aria-hidden="true" className={cx('grid size-[22px] shrink-0 place-items-center rounded-full border-2', selected ? 'border-fg bg-fg text-bg' : 'border-line-strong')}>
        {selected && <Icon name="check" size={12} strokeWidth={3.5} />}
      </span>
    </button>
  );
}

function SignedIn({ me }: { me: Me }) {
  const showPro = useShowPro();
  const pending = usePendingDocuments();
  const invest = useInvestItems();
  const kind = useAccountKind();
  const [copied, setCopied] = useState(false);
  const live = me.accounts.find((a) => a.kind === 'live');
  const accounts = [...me.accounts].sort((a, b) => (a.kind === 'live' ? -1 : b.kind === 'live' ? 1 : 0));
  // A staff session that may not see the email gets it masked: no use as a name.
  const name = [me.firstName, me.lastName].filter(Boolean).join(' ') || (me.contactHidden ? 'Client' : me.email);
  const initials = (me.firstName && me.lastName ? me.firstName[0]! + me.lastName[0]! : me.email.slice(0, 2)).toUpperCase();
  const [kycLabel, kycCls] = KYC[me.kycStatus] ?? KYC.none;
  const verified = me.kycStatus === 'approved';
  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(t);
  }, [copied]);

  return (
    <>
      <div className="flex items-center gap-3.5 px-5 pt-6">
        <span aria-hidden="true" className="grid size-[52px] shrink-0 place-items-center rounded-full bg-accent-bg text-[18px] font-semibold text-accent-fg">
          {initials}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-[19px] font-semibold">{name}</span>
            <Link to="/account/kyc" onClick={closeMenu} className={cx('shrink-0 rounded-full px-2.5 py-0.5 text-[13px] font-semibold no-underline', kycCls)}>
              {kycLabel}
            </Link>
          </div>
          {live && (
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard?.writeText(live.id);
                setCopied(true);
              }}
              aria-label="Copy account ID"
              className="tnum mt-0.5 inline-flex items-center gap-1.5 text-[14px] text-muted"
            >
              {copied ? 'Copied' : shortAccountId(live.id)}
              <Icon name={copied ? 'check' : 'copy'} size={14} />
            </button>
          )}
        </div>
      </div>

      <h2 className="m-0 mt-7 mb-2 px-5 text-[15px] font-semibold text-muted">Accounts</h2>
      <div role="radiogroup" aria-label="Trading account" className="mx-4 flex flex-col gap-1 rounded-[22px] bg-track p-1.5">
        {accounts.filter((a) => a.kind !== 'demo').map((a) => (
          <AccountOption key={a.id} a={a} selected={a.kind === kind} />
        ))}
        <MobileDemoRow me={me} />
      </div>

      <div className="mx-4 mt-4 grid grid-cols-2 gap-2.5">
        <Link to="/account/deposit" onClick={closeMenu} className="flex h-[52px] items-center justify-center gap-2 rounded-full bg-fg text-[16px] font-semibold text-bg no-underline">
          <Icon name="down" size={18} strokeWidth={2.4} />
          Deposit
        </Link>
        <Link to="/account/withdraw" onClick={closeMenu} className="flex h-[52px] items-center justify-center gap-2 rounded-full bg-pill text-[16px] font-semibold text-fg no-underline">
          Withdraw
          {!verified && (
            <>
              <Icon name="lock" size={16} />
              <span className="sr-only">(verify your identity first)</span>
            </>
          )}
        </Link>
      </div>

      {/* The Invest and More menus' picture cards, as a row that scrolls sideways. */}
      <h2 className="m-0 mt-6 mb-2 px-5 text-[15px] font-semibold text-muted">Explore</h2>
      <div className="flex gap-3 overflow-x-auto px-5 pb-1 [scrollbar-width:none]">
        {[...invest.map((i) => i.column.promo), REWARDS_PROMO].map((p) => (
          <PromoCard
            key={p.to}
            promo={p}
            onClick={() => {
              closeMenu();
              navigate(p.to);
            }}
            className="w-[78%] max-w-[320px] shrink-0"
          />
        ))}
      </div>

      {invest.length > 0 && (
        <>
          <h2 className="m-0 mt-6 mb-0 px-5 text-[15px] font-semibold text-muted">Invest</h2>
          <nav aria-label="Invest" className="mx-5">
            {invest.map((i) => (
              <MenuLink key={i.to} to={i.to} icon={i.icon}>
                {i.label}
              </MenuLink>
            ))}
          </nav>
        </>
      )}

      <nav aria-label="Account" className="mx-5 mt-4">
        <MenuLink to="/account/personal" icon="user">
          Personal information
        </MenuLink>
        <MenuLink to="/account/security" icon="shield">
          Security &amp; 2FA
        </MenuLink>
        <MenuLink to="/account/documents" icon="docs">
          Documents
          {pending > 0 && <span className="num ml-2 inline-grid h-5 min-w-5 place-items-center rounded-full bg-warn px-1.5 align-middle text-[11px] font-semibold text-white">{pending}</span>}
        </MenuLink>
        <MenuLink to="/markets/calendar" icon="calendar">
          Economic calendar
        </MenuLink>
        <MenuLink to="/academy" icon="docs">
          Academy
        </MenuLink>
        <MenuLink to="/earn" icon="star">
          Rewards &amp; referrals
        </MenuLink>
        <MenuLink to="/earn/yield" icon="wallet">
          Yield
        </MenuLink>
        {showPro && (
          <MenuLink to="/pro" icon="zap">
            Crypto Pro
          </MenuLink>
        )}
        <MenuLink to="/account/alerts" icon="bell">
          Alerts
        </MenuLink>
        <MenuLink to="/account/support" icon="chat">
          Support
        </MenuLink>
        <MenuLink to="/risk-disclosure" icon="docs">
          Legal &amp; risk disclosure
        </MenuLink>
        {(me.role === 'admin' || me.role === 'dealer') && (
          <MenuLink to="/admin" icon="grid">
            Admin
          </MenuLink>
        )}
        <InstallRow />
        <DarkModeRow />
        <button
          type="button"
          onClick={() => {
            closeMenu();
            void logout().then(() => navigate('/welcome'));
          }}
          className="flex h-[54px] w-full items-center gap-3.5 text-left text-[17px] font-semibold text-down"
        >
          <Icon name="logout" size={22} />
          Log out
        </button>
      </nav>
    </>
  );
}

function SignedOut() {
  return (
    <>
      <div className="px-5 pt-7">
        <span className="text-[19px] font-semibold tracking-tight">{BRAND.name}</span>
        <p className="m-0 mt-3 text-[16px] text-muted">Create an account or log in to start trading.</p>
      </div>
      <div className="mx-4 mt-5 flex flex-col gap-2.5">
        <Link to="/register" onClick={closeMenu} className="flex h-[52px] items-center justify-center rounded-full bg-fg text-[16px] font-semibold text-bg no-underline">
          Create account
        </Link>
        <Link to="/login" onClick={closeMenu} className="flex h-[52px] items-center justify-center rounded-full border border-line text-[16px] font-semibold text-fg no-underline">
          Log in
        </Link>
      </div>
      <nav aria-label="Site" className="mx-5 mt-4">
        <MenuLink to="/markets" icon="bars">
          Markets
        </MenuLink>
        <MenuLink to="/risk-disclosure" icon="docs">
          Legal &amp; risk disclosure
        </MenuLink>
        <InstallRow />
        <DarkModeRow />
      </nav>
    </>
  );
}

export function MobileMenu() {
  const open = useMenuOpen();
  const me = useMe();
  const path = usePath();
  const closeBtn = useRef<HTMLButtonElement>(null);

  // Any navigation closes the menu.
  useEffect(() => closeMenu(), [path]);

  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeBtn.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeMenu();
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      prev?.focus?.();
    };
  }, [open]);

  if (!open) return null;
  return (
    <div role="dialog" aria-modal="true" aria-label="Menu" className="sheet-in no-scrollbar fixed inset-0 z-50 overflow-x-hidden overflow-y-auto overscroll-contain bg-bg text-fg">
      <div className="mx-auto w-full max-w-[640px] pb-[max(32px,env(safe-area-inset-bottom))]">
        <header className="px-4 pt-[calc(env(safe-area-inset-top)+12px)]">
          <button
            ref={closeBtn}
            type="button"
            onClick={closeMenu}
            className={cx('inline-flex h-12 items-center gap-3 rounded-full pr-[22px] pl-[18px] text-[17px] font-semibold', floatCls)}
          >
            <Icon name="close" size={22} strokeWidth={2.2} />
            Close
          </button>
        </header>
        {me ? <SignedIn me={me} /> : <SignedOut />}
      </div>
    </div>
  );
}
